import { useState, useEffect, useRef, useCallback } from 'react';
import { Signal, SignalFilters, SignalsStats } from '../types';
import api from '../services/api';

export type { SignalsStats };

export const useFetchSignals = (page: number = 1, filters?: SignalFilters, limit = 20) => {
  const [signals, setSignals] = useState<Signal[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<SignalsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef(filters);
  const filtersKey = JSON.stringify(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    void filtersKey;
    try {
      setLoading(true);
      setError(null);
      const response = await api.signals.list(page, filtersRef.current, limit);
      setSignals(response.data.signals);
      setTotal(response.data.pagination.total);
      if (response.data.stats) {
        setStats(response.data.stats);
      } else {
        setStats({
          total: response.data.pagination.total,
          actionRequired: response.data.signals.filter(
            (s: Signal) => s.triage_status === 'requires_action' || !s.triage_status
          ).length,
          critical: response.data.signals.filter((s: Signal) => s.urgency === 'critical').length,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            'Failed to fetch signals';
      setError(message);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  }, [page, filtersKey, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { signals, total, stats, loading, error, refetch: fetchData };
};
